import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Sparkles, Wallet, Home as HomeIcon, MapPin, Plane, Hotel, Shield, Calendar, Headphones, Instagram, Facebook, Youtube } from "lucide-react";
import RazorpayTrustBadge from "@/components/RazorpayTrustBadge";
import SSLVerifiedBadge from "@/components/SSLVerifiedBadge";
import { Button } from "@/components/ui/button";

const PILLARS = [
  {
    icon: Compass,
    title: "Curated Itineraries",
    body: "Every destination is hand-picked and paired with day-wise activities — including hidden gems most guides miss.",
  },
  {
    icon: Sparkles,
    title: "Live Pricing",
    body: "Real-time flight and hotel rates from Google Flights & Google Hotels. No padded markups, no surprises at checkout.",
  },
  {
    icon: Wallet,
    title: "Flexible Payments",
    body: "Pay 1% to lock the trip today, then settle the rest in weekly, bi-weekly or monthly installments — or all at once.",
  },
];

const REASONS = [
  { icon: Plane, label: "Real-time Google Flights & Hotels pricing" },
  { icon: Calendar, label: "AI-driven day-wise itineraries & hidden gems" },
  { icon: Hotel, label: "3 package tiers: Luxury, Premium, Basic" },
  { icon: Wallet, label: "Book-now-pay-later installment plans" },
  { icon: Headphones, label: "Dedicated booking support at admin@travelsate.in or +91 99727 44308" },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050508] text-white" data-testid="about-page">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-50 bg-black/60 backdrop-blur-md border-b border-white/5">
        <div className="container mx-auto px-4 flex items-center justify-between h-14">
          <button
            onClick={() => navigate("/")}
            className="flex items-center"
            data-testid="about-logo-link"
          >
            <img src="/logo.svg" alt="Travelsate" className="h-7" />
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-white/70 hover:text-white hover:bg-white/5 gap-1.5"
              data-testid="about-home-button"
            >
              <HomeIcon className="w-4 h-4" />
              Home
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/booking")}
              className="bg-transparent border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]"
              data-testid="about-book-button"
            >
              Start Planning
            </Button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url(https://images.pexels.com/photos/2549018/pexels-photo-2549018.jpeg?auto=compress&cs=tinysrgb&w=1920)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050508]/70 via-[#050508]/85 to-[#050508]" />
        <div className="relative container mx-auto px-6 py-24 md:py-32 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[#D4AF37]/70 text-[11px] tracking-[0.3em] uppercase font-bold italic mb-4">
              About Travelsate
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mb-6">
              Journeys that sate <br />
              <span className="text-[#D4AF37]">your wanderlust.</span>
            </h1>
            <p className="text-white/60 text-base max-w-2xl leading-relaxed">
              Travelsate is a curated travel platform for travellers who want
              every detail handled — without sacrificing transparency on price
              or freedom of choice.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="container mx-auto px-6 py-20 md:py-24 max-w-4xl">
        <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase font-bold italic mb-3">
          Our Story
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl mb-8">
          Travel, refined for the modern explorer.
        </h2>
        <div className="space-y-5 text-white/70 leading-relaxed text-base">
          <p>
            Travelsate was born out of a simple frustration — booking a great
            trip shouldn't feel like a second job. Aggregators show inflated
            prices. Travel agents push the same templated tours. Researching
            flights, hotels, transfers, and itineraries across a dozen tabs
            kills the very excitement that should come with planning a journey.
          </p>
          <p>
            We built Travelsate to fix that. Every destination on our platform
            is pre-curated. Every price you see is pulled live from Google
            Flights and Google Hotels — the same data the airlines and hotels
            themselves publish. Our AI layer reads thousands of real Google
            reviews to tell you whether a place is safer for solo female
            travellers, friendlier for elderly parents, or better suited for a
            family with young kids. And once you decide, you can lock the trip
            for just 1% today and pay the rest over time.
          </p>
        </div>
      </section>

      {/* What We Do */}
      <section className="border-y border-white/5 bg-white/[0.015]">
        <div className="container mx-auto px-6 py-20 md:py-24 max-w-6xl">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase font-bold italic mb-3">
            What We Do
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-12 max-w-2xl">
            Three pillars, one seamless trip.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                className="bg-white/[0.03] border border-white/10 rounded-xl p-7 hover:border-[#D4AF37]/30 transition-colors"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center mb-5">
                  <p.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-xl mb-3">{p.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="container mx-auto px-6 py-20 md:py-24 max-w-4xl">
        <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase font-bold italic mb-3">
          Why Travelsate
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl mb-12">
          Built for travellers who notice the details.
        </h2>
        <ul className="space-y-4">
          {REASONS.map((r) => (
            <li
              key={r.label}
              className="flex items-start gap-4 py-4 border-b border-white/5"
              data-testid={`reason-${r.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <r.icon className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <p className="text-white/80 text-base leading-relaxed">{r.label}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Contact Strip */}
      <section className="border-t border-white/5 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 py-16 max-w-3xl text-center">
          <MapPin className="w-7 h-7 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="font-serif text-2xl sm:text-3xl mb-3">
            Have a question? We're listening.
          </h3>
          <p className="text-white/55 text-sm mb-6 max-w-md mx-auto">
            For booking queries, change requests, or partnership enquiries, reach us at
          </p>
          <a
            href="mailto:admin@travelsate.in"
            data-testid="about-contact-email"
            className="inline-block text-[#D4AF37] text-base sm:text-lg tracking-wide border-b border-[#D4AF37]/30 hover:border-[#D4AF37] transition-colors pb-0.5"
          >
            admin@travelsate.in
          </a>
          <p className="text-white/40 text-sm mt-3">
            Or call us at{" "}
            <a
              href="tel:+919972744308"
              data-testid="about-contact-phone"
              className="text-[#D4AF37] hover:underline"
            >
              +91 99727 44308
            </a>
          </p>

          {/* "Follow us" pills — reuse each platform's brand colour on hover
              so they read instantly as social CTAs without explicit
              "Instagram"/"Facebook" wordmarks. Travel buyers expect to vet
              the operator on social before committing, so this section
              earns its prominence in the contact strip. */}
          <div className="mt-7 flex flex-col items-center gap-3">
            <span className="text-white/40 text-sm">See our latest journeys on</span>
            <div className="inline-flex flex-wrap items-center justify-center gap-2.5">
              <a
                href="https://www.instagram.com/travelsate_letsgo/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-link"
                className="group inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] hover:border-transparent hover:bg-gradient-to-r hover:from-[#feda75] hover:via-[#d62976] hover:to-[#4f5bd5] hover:text-white text-white/80 transition-all duration-300"
              >
                <Instagram className="w-4 h-4" />
                <span className="text-sm font-medium tracking-wide">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61591381741161"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-facebook-link"
                className="group inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] hover:border-transparent hover:bg-[#1877F2] hover:text-white text-white/80 transition-all duration-300"
              >
                <Facebook className="w-4 h-4" />
                <span className="text-sm font-medium tracking-wide">Facebook</span>
              </a>
              <a
                href="https://www.youtube.com/@travelsate_letsgo"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-youtube-link"
                className="group inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-white/15 bg-white/[0.03] hover:border-transparent hover:bg-[#FF0000] hover:text-white text-white/80 transition-all duration-300"
              >
                <Youtube className="w-4 h-4" />
                <span className="text-sm font-medium tracking-wide">YouTube</span>
              </a>
            </div>
          </div>

          <div className="mt-10">
            <Button
              onClick={() => navigate("/booking")}
              className="bg-[#D4AF37] text-black hover:bg-[#E5C468] font-medium"
              data-testid="about-start-planning-cta"
            >
              Start Planning Your Trip
            </Button>
          </div>
        </div>
      </section>

      {/* Razorpay + SSL trust badges paired — final reassurance before footer. */}
      <section className="border-t border-white/5 bg-black/80 py-10" data-testid="about-trust-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <RazorpayTrustBadge variant="full" />
            <SSLVerifiedBadge variant="full" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-black">
        <div className="container mx-auto px-6 py-8 max-w-6xl flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-3.5 h-3.5 text-white/30" />
            <p className="text-white/30 text-xs tracking-wide">
              © {new Date().getFullYear()} Travelsate. Your extraordinary journey awaits.
            </p>
          </div>
          <div className="flex items-center gap-5 text-xs">
            <button onClick={() => navigate("/")} className="text-white/50 hover:text-white transition-colors" data-testid="footer-home">
              Home
            </button>
            <button onClick={() => navigate("/about")} className="text-white/50 hover:text-white transition-colors" data-testid="footer-about">
              About Us
            </button>
            <a href="mailto:admin@travelsate.in" className="text-white/50 hover:text-white transition-colors">
              admin@travelsate.in
            </a>
            <a href="tel:+919972744308" className="text-white/50 hover:text-white transition-colors" data-testid="footer-phone">
              +91 99727 44308
            </a>
            <a
              href="https://www.instagram.com/travelsate_letsgo/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Travelsate on Instagram"
              data-testid="about-footer-instagram-link"
              className="group inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-white/50 hover:text-white hover:border-transparent hover:bg-gradient-to-br hover:from-[#feda75] hover:via-[#d62976] hover:to-[#4f5bd5] transition-all"
            >
              <Instagram className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61591381741161"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow Travelsate on Facebook"
              data-testid="about-footer-facebook-link"
              className="group inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-white/50 hover:text-white hover:border-transparent hover:bg-[#1877F2] transition-all"
            >
              <Facebook className="w-3.5 h-3.5" />
            </a>
            <a
              href="https://www.youtube.com/@travelsate_letsgo"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Subscribe to Travelsate on YouTube"
              data-testid="about-footer-youtube-link"
              className="group inline-flex items-center justify-center w-7 h-7 rounded-md border border-white/10 text-white/50 hover:text-white hover:border-transparent hover:bg-[#FF0000] transition-all"
            >
              <Youtube className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
